import { AssessmentAnswerInterface } from 'app/assessment/models';
import { EquipmentInterface } from 'app/equipment/models/equipment.model';
import { EquipmentActions } from 'app/equipment/state/actions';
import { EquipmentActionType } from 'app/equipment/state/actions/equipment-action-type.enum';
import { EquipmentState, EquipmentStateInterface } from 'app/equipment/state/models';
import { sortEquipmentItemsByTitle } from 'app/shared/utilities/object-sort-methods.utility';

import {
  EmployeeEquipmentWorkflowInterface,
  EmployeeEquipmentWorkflowStatusTypeInterface,
  EquipmentCategoryInterface } from '../models';



const initialState: EquipmentState = new EquipmentState({
  equipmentLibrary: [],
  recommendedEquipmentLibrary: [],
  statusTypes: [],
  workAreas: [],
  currentUserEquipmentWorkflows: [],
  selectedWorkFlow: {},
  selectedEquipment: {},
  libraryLoading: false,
});

export function equipmentReducer(state: EquipmentStateInterface = initialState, action: EquipmentActions): EquipmentStateInterface {
  switch (action.type) {

    // Load the equipment library(s)
    case EquipmentActionType.LoadEquipmentLibrary:
    case EquipmentActionType.LoadEquipmentLibraryFailed:
      return {
        ...state,
        equipmentLibrary: [],
        recommendedEquipmentLibrary: [],
        libraryLoading: true,
      };

    case EquipmentActionType.LoadEquipmentLibraryCompleted:
      const allEquipment: EquipmentCategoryInterface[] = action.payload as EquipmentCategoryInterface[];
      let recommendedEquipment: EquipmentCategoryInterface[] = allEquipment.map((category: EquipmentCategoryInterface) => {
        const qualifiedItems: EquipmentInterface[] = category.items.filter((item: EquipmentInterface) => {
          if(item.canRecommend) {
            return item;
          }
        });
        return {
          ...category,
          items: qualifiedItems,
        };
      });
      recommendedEquipment = recommendedEquipment.filter((category: EquipmentCategoryInterface) =>
        category && category.items && category.items.length > 0);
      return {
        ...state,
        equipmentLibrary: allEquipment,
        recommendedEquipmentLibrary: recommendedEquipment,
        libraryLoading: false,
      };

    // Load the status type data
    case EquipmentActionType.LoadWorkflowStatusTypes:
    case EquipmentActionType.LoadWorkflowStatusTypesFailed:
      return {
        ...state,
        statusTypes: [],
      };

    case EquipmentActionType.LoadWorkflowStatusTypesCompleted:
      const statusTypeList: EmployeeEquipmentWorkflowStatusTypeInterface[] =
        action.payload as EmployeeEquipmentWorkflowStatusTypeInterface[];

      return {
        ...state,
        statusTypes: statusTypeList,
      };

    // Load the work area data
    case EquipmentActionType.LoadWorkAreas:
    case EquipmentActionType.LoadWorkAreasFailed:
      return {
        ...state,
        workAreas: [],
      };

    case EquipmentActionType.LoadWorkAreasCompleted:
      const workAreaList: AssessmentAnswerInterface[] = action.payload as AssessmentAnswerInterface[];

      return {
        ...state,
        workAreas: workAreaList,
      };

    // Load single library item
    case EquipmentActionType.LoadEquipmentLibraryItem:
      return {
        ...state,
        selectedEquipment: {},
      };

    case EquipmentActionType.LoadEquipmentLibraryItemCompleted:
      const equipmentItem: EquipmentInterface = action.payload as EquipmentInterface;

      return {
        ...state,
        selectedEquipment: equipmentItem,
      };

    // Load single employee's equipment workflow list
    case EquipmentActionType.LoadEmployeeEquipmentWorkflows:
    case EquipmentActionType.LoadEmployeeEquipmentWorkflowsFailed:
      return {
        ...state,
        currentUserEquipmentWorkflows: [],
      };

    case EquipmentActionType.LoadEmployeeEquipmentWorkflowsCompleted:
      const workflows: EmployeeEquipmentWorkflowInterface[] = action.payload as EmployeeEquipmentWorkflowInterface[];

      return {
        ...state,
        currentUserEquipmentWorkflows: workflows,
      };

    // Equipment library updates
    case EquipmentActionType.UpdateEquipmentCategoryCompleted:
      const updatedCategoryData: EquipmentCategoryInterface = action.payload as EquipmentCategoryInterface;

      const updatedLibrary: EquipmentCategoryInterface[] = state.equipmentLibrary.map((category: EquipmentCategoryInterface) => {
        if(category.id === updatedCategoryData.id) {
          return {
            ...category,
            title: updatedCategoryData.title,
          };
        }
        return category;
      });

      return {
        ...state,
        equipmentLibrary: updatedLibrary,
      };

    case EquipmentActionType.CreateEquipmentLibraryItemCompleted:
    case EquipmentActionType.UpdateEquipmentLibraryItemCompleted:
      const updatedEquipmentItem: EquipmentInterface = action.payload as EquipmentInterface;

      const updatedLibraryEquipment: EquipmentCategoryInterface[] = state.equipmentLibrary.map((category: EquipmentCategoryInterface) => {
        if(updatedEquipmentItem.categoryId === category.id) {
          let found: boolean = false;
          let updatedItems: EquipmentInterface[] = category.items.map((item: EquipmentInterface) => {
            if(item.id === updatedEquipmentItem.id) {
              found = true;
              return {
                ...item,
                title:  updatedEquipmentItem.title,
                manufacturer:  updatedEquipmentItem.manufacturer,
                modelNumber:  updatedEquipmentItem.modelNumber,
                color:  updatedEquipmentItem.color,
                cost:  updatedEquipmentItem.cost,
                description: updatedEquipmentItem.description,
                productUrl:  updatedEquipmentItem.productUrl,
                imageRef: updatedEquipmentItem.imageRef,
                canRecommend:  updatedEquipmentItem.canRecommend,
              };
            }
            return item;
          });
          if(!found) {
            updatedItems.push(updatedEquipmentItem);
            updatedItems = sortEquipmentItemsByTitle(updatedItems);
          }
          return {
            ...category,
            items: updatedItems,
          };
        }
        return {
          ...category,
          items: category.items ?
            category.items.filter((item: EquipmentInterface) => item.id !== updatedEquipmentItem.id) : [],
        };
      });

      return {
        ...state,
        equipmentLibrary: updatedLibraryEquipment,
        selectedEquipment: updatedEquipmentItem,
      };

    // Employee Equipment workflow updates
    case EquipmentActionType.UpdateEmployeeEquipmentItemCompleted:
    case EquipmentActionType.UpdateEmployeeEquipmentWorkFlowStatusCompleted:
    case EquipmentActionType.AddNewStatusToEmployeeEquipmentWorkflowCompleted:
      const updatedWorkflow: EmployeeEquipmentWorkflowInterface = action.payload as EmployeeEquipmentWorkflowInterface;

      const updatedEmployeeWorkflows: EmployeeEquipmentWorkflowInterface[] = state.currentUserEquipmentWorkflows.map(
        (workflow: EmployeeEquipmentWorkflowInterface) => {
          if(workflow.id === updatedWorkflow.id) {
            return updatedWorkflow;
          }

          return workflow;
        });

      return {
        ...state,
        selectedWorkFlow: updatedWorkflow,
        currentUserEquipmentWorkflows: updatedEmployeeWorkflows,
      };

    case EquipmentActionType.CreateEmployeeEquipmentWorkflowCompleted:
      const newWorkflow: EmployeeEquipmentWorkflowInterface = action.payload as EmployeeEquipmentWorkflowInterface;

      const extendedWorkflowList: EmployeeEquipmentWorkflowInterface[] =
        state.currentUserEquipmentWorkflows.map((workFlow: EmployeeEquipmentWorkflowInterface) => workFlow);
      extendedWorkflowList.push(newWorkflow);

      return {
        ...state,
        currentUserEquipmentWorkflows: extendedWorkflowList,
      };

    default:
      return state;
  }
}
